/**
 * Отложенное действие (MVP_TECH_RFC §18, MVP_PRD §8).
 *
 * Гость нажал «в избранное» или «сравнить» → действие запоминается, открывается
 * вход, после успеха действие выполняется автоматически и пользователь
 * возвращается туда, откуда начал.
 *
 * `sessionStorage` здесь допустим по §22: это намерение пользователя, а не
 * секрет. **Токен авторизации в хранилище не попадает никогда** — он живёт в
 * HttpOnly cookie, недоступной JavaScript.
 */

const STORAGE_KEY = 'carviq.pending-action';

export type PendingActionType = 'assistant.start' | 'favorite.add' | 'comparison.add' | 'navigate';

export interface PendingAction {
  type: PendingActionType;
  /** Куда вернуть пользователя после входа. Только внутренний путь. */
  returnUrl: string;
  vehicleId?: string;
}

function isSafeReturnUrl(value: unknown): value is string {
  // Только относительный путь. `//evil.example` тоже отвергается: браузер
  // трактует его как протокол-относительный адрес и увёл бы на чужой домен.
  return typeof value === 'string' && value.startsWith('/') && !value.startsWith('//');
}

export function savePendingAction(action: PendingAction): void {
  try {
    sessionStorage.setItem(STORAGE_KEY, JSON.stringify(action));
  } catch {
    // Приватный режим Safari запрещает запись. Вход от этого не ломается:
    // после успеха пользователь просто останется на текущей странице.
  }
}

export function readPendingAction(): PendingAction | null {
  let raw: string | null = null;
  try {
    raw = sessionStorage.getItem(STORAGE_KEY);
  } catch {
    return null;
  }
  if (!raw) return null;

  try {
    const parsed: unknown = JSON.parse(raw);
    if (typeof parsed !== 'object' || parsed === null) return null;

    const { type, returnUrl, vehicleId } = parsed as Record<string, unknown>;
    if (typeof type !== 'string' || !isSafeReturnUrl(returnUrl)) return null;

    return {
      type: type as PendingActionType,
      returnUrl,
      ...(typeof vehicleId === 'string' ? { vehicleId } : {}),
    };
  } catch {
    return null;
  }
}

export function clearPendingAction(): void {
  try {
    sessionStorage.removeItem(STORAGE_KEY);
  } catch {
    // См. комментарий в savePendingAction.
  }
}

/** Прочитать и сразу удалить: действие выполняется ровно один раз. */
export function takePendingAction(): PendingAction | null {
  const action = readPendingAction();
  clearPendingAction();
  return action;
}
